import ContentPasteSearchOutlined from "@mui/icons-material/ContentPasteSearchOutlined";
import FilterListOffOutlined from "@mui/icons-material/FilterListOffOutlined";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../components/UIToolkit/Animations/JumpingDots";
import { SystemChoiceList } from "../../../../../components/UIToolkit/Clickable/SystemChoiceList";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { EmptyState } from "../../../../../components/UIToolkit/molecules/EmptyState";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { CHOICE_PICKER, FONT_SIZE } from "../../../../../theme/tokens";
import { AirtableFacilityRow, facilityLabel } from "./AirtableFacilityRow";
import type { AirtableFacilityMatch } from "./airtableFacilityFilter";
import { AIRTABLE_FACILITY_SEARCH_MIN_LENGTH } from "./airtableImportHooks";
import type { AirtableFacilityOption } from "./airtableImportTypes";

type Props = {
	query: string;
	onQueryChange: (query: string) => void;
	/** The loaded records the query narrows to, in the server's order, with the matched runs located. */
	matches: AirtableFacilityMatch[];
	/** How many records were loaded in total — the denominator the filtered count is honest against. */
	totalCount: number;
	/** The ONE up-front fetch is in flight. Never true while typing: filtering is in memory. */
	isLoading: boolean;
	errorMessage: string | null;
	/** The loaded list is a partial read of the base, so the query is ALSO searched on Airtable's side. */
	truncated: boolean;
	/** That Airtable-side search is in flight — an empty result now is not an answer yet. */
	isSearchingBase: boolean;
	/** That search failed. Shown beside the loaded list rather than instead of it: what loaded is still good. */
	baseSearchErrorMessage: string | null;
	/**
	 * Why the last Continue failed to build the diff, already translated. Continue only advances once the
	 * diff is in hand, so a failed preview leaves the operator here — and has to say so here.
	 */
	previewErrorMessage: string | null;
	/** Facility names more than one loaded record shares — those rows also show the record's own title. */
	ambiguousFacilityNames: ReadonlySet<string>;
	/** The chosen record, carried whole so the confirmation line survives the query that filters it out. */
	selectedFacility: AirtableFacilityOption | null;
	onSelect: (recordId: string) => void;
};

/**
 * Step 1 — find the implementation-intake record.
 *
 * A type-ahead over the WHOLE base rather than a search that queries it: the list is fetched once when
 * the step opens and every keystroke filters it in memory, so the list narrows as fast as the operator
 * types and an empty box shows everything instead of a "type to search" placeholder.
 *
 * A search box over a choice list rather than a dropdown, because the two things that identify a record
 * are the facility name AND the kickoff record it came from, and two facilities in the same network
 * routinely differ only in the second. A dropdown truncates exactly the part that disambiguates them;
 * here the kickoff title gets its own line, and whichever part of either matched is emphasized, so a
 * pair of near-identical rows says on its face why each one is still on screen.
 *
 * Four outcomes that must not share one message: the list is still loading · the base came back empty ·
 * the filter matched nothing · nothing matched AND the list we filtered is itself partial.
 */
export const AirtableRecordStep: FC<Props> = ({
	query,
	onQueryChange,
	matches,
	totalCount,
	isLoading,
	errorMessage,
	truncated,
	isSearchingBase,
	baseSearchErrorMessage,
	previewErrorMessage,
	ambiguousFacilityNames,
	selectedFacility,
	onSelect,
}) => {
	const { t } = useTranslation();
	const trimmed = query.trim();

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
			{previewErrorMessage && (
				<SystemBanner
					tone="error"
					title={t("department.config.airtable.error.previewTitle")}
					description={previewErrorMessage}
					dataTestId="airtable-preview-error-banner"
				/>
			)}
			<SystemValidationTextField
				label={t("department.config.airtable.search.label")}
				value={query}
				onChange={(event) => onQueryChange(event.target.value)}
				placeholder={t("department.config.airtable.search.placeholder")}
				fullWidth
				autoFocus
				dataTestId="airtable-facility-search"
			/>
			{errorMessage && (
				<InlineHint tone="warning" dataTestId="airtable-facility-error">
					{errorMessage}
				</InlineHint>
			)}
			{truncated && !isLoading && !errorMessage && (
				// Not a footnote under the results: this is the one state where typing a real facility's
				// name can honestly come back empty, so it has to be readable before the operator concludes
				// the record does not exist.
				<SystemBanner
					tone="warning"
					icon={<LayersOutlined />}
					title={t("department.config.airtable.search.truncated.title")}
					description={t("department.config.airtable.search.truncated.body", { count: totalCount })}
					dataTestId="airtable-facility-truncated"
				/>
			)}

			{baseSearchErrorMessage && (
				<InlineHint tone="warning" dataTestId="airtable-facility-base-search-error">
					{baseSearchErrorMessage}
				</InlineHint>
			)}

			{isLoading ? (
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 4 }} data-testid="airtable-facility-loading">
					<JumpingDots />
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>{t("department.config.airtable.search.loading")}</Typography>
				</Box>
			) : errorMessage ? null : totalCount === 0 ? (
				// An empty BASE is not a failed search: there is nothing to type at, so saying "check the
				// spelling" would send the operator hunting for their own mistake.
				<EmptyState
					icon={<ContentPasteSearchOutlined />}
					title={t("department.config.airtable.search.emptyBase.title")}
					hint={t("department.config.airtable.search.emptyBase.hint")}
					dataTestId="airtable-facility-empty-base"
				/>
			) : (
				<>
					{/* Suppressed on an empty result, where the empty state below already says it and a
					    "0 of 14" strip only adds a second voice saying the same thing. */}
					{matches.length > 0 && (
						<Typography
							data-testid="airtable-facility-count"
							sx={{
								fontSize: FONT_SIZE.LABEL_XS,
								fontWeight: 700,
								letterSpacing: "0.06em",
								textTransform: "uppercase",
								color: "text.secondary",
							}}
						>
							{trimmed.length > 0
								? t("department.config.airtable.search.count.filtered", { shown: matches.length, total: totalCount })
								: t("department.config.airtable.search.count.all", { count: totalCount })}
						</Typography>
					)}
					{matches.length === 0 && isSearchingBase ? (
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, py: 4 }} data-testid="airtable-facility-searching">
							<JumpingDots />
							<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }}>{t("department.config.airtable.search.searchingBase")}</Typography>
						</Box>
					) : matches.length === 0 ? (
						<EmptyState
							icon={<FilterListOffOutlined />}
							title={t("department.config.airtable.search.noResults.title")}
							hint={t(
								!truncated
									? "department.config.airtable.search.noResults.hint"
									: trimmed.length >= AIRTABLE_FACILITY_SEARCH_MIN_LENGTH
										? "department.config.airtable.search.noResults.truncatedHint"
										: "department.config.airtable.search.noResults.truncatedShortHint",
							)}
							dataTestId="airtable-facility-empty"
						/>
					) : (
						// The list scrolls inside a bounded box rather than growing the step: a base of tens of
						// records would otherwise push the search box off the top of the dialog, which is the one
						// control the operator is using.
						<Box sx={{ maxHeight: CHOICE_PICKER.LIST_MAX_HEIGHT, overflowY: "auto", minWidth: 0, pr: 0.5 }}>
							<SystemChoiceList
								items={matches.map((match) => ({
									value: match.option.recordId,
									// The flat string, so the row's accessible name is unaffected by how it is laid out or highlighted.
									label: facilityLabel(match.option, ambiguousFacilityNames, t),
									content: <AirtableFacilityRow match={match} ambiguousNames={ambiguousFacilityNames} />,
									dataTestId: `airtable-facility-${match.option.recordId}`,
								}))}
								onSelect={onSelect}
								selectedValue={selectedFacility?.recordId ?? null}
								ariaLabel={t("department.config.airtable.search.results.ariaLabel")}
								dataTestId="airtable-facility-list"
							/>
						</Box>
					)}
				</>
			)}

			{selectedFacility && (
				// Restated below the list because the chosen row can be scrolled out of sight — or filtered
				// out of the list entirely by the next thing the operator types, which does NOT unpick it.
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary" }} data-testid="airtable-facility-selected">
					{t("department.config.airtable.search.selected", { facility: facilityLabel(selectedFacility, ambiguousFacilityNames, t) })}
				</Typography>
			)}
		</Box>
	);
};
